import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, X, Search, ArrowLeft,
  ChevronRight, ChevronDown, Network, Upload, Download, FileText,
  History, Ban, RotateCcw,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SortableHeader, { usePagedSortableData, TablePager } from '../components/SortableTable';
import WorkflowRefLink from '../components/shared/WorkflowRefLink';
import { provenanceLabel } from '../components/shared/provenance';
import ClientEntityHistory from '../components/shared/ClientEntityHistory';
import ResultCount from '../components/shared/ResultCount';
import SegmentedSwitch from '../components/shared/SegmentedSwitch';
import {
  Field, FilePicker, inputCls, btnPrimary, btnGhost, modalOverlayCls, LockTip, FormModal,
} from '../components/shared/FormKit';
import SearchSelect from '../components/shared/SearchSelect';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import FileList, { DownloadLink } from '../components/shared/FileList';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { firmToday } from '../utils/dateRange';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { exportToExcel } from '../utils/excelExport';
import {
  EntityCategorySelect, EntityCategoryChip, RelationshipBadge, uenHintFor,
  ENTITY_CATEGORIES, ENTITY_CATEGORY_LABELS,
} from '../components/shared/entityCategory';
import { exportExcelBtnCls } from '../components/shared/ExportExcelButton';

const STATUS_FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'all', label: 'All' },
];

const RELATIONSHIPS = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

// Relationship is read from `active`; an inactive company always carries its
// inactive date (client_entities_inactive_check), which cannot lie in the future.
const inactiveDateError = (inactiveDate) => (
  inactiveDate && inactiveDate > firmToday() ? 'The inactive date cannot be in the future.' : null
);

// A group is active while any of its companies is (derived — there is no
// switch on the group itself).
const StatusBadge = ({ active }) => <RelationshipBadge entity={{ active }} />;

// Whole master to Excel, ignoring the toolbar filters: one row per entity,
// grouped companies by group then entity name, ungrouped last. An empty group
// still gets a row so every group is in the file.
const exportAllToExcel = (groups, entities) => {
  const byName = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });
  const groupName = new Map(groups.map((g) => [g.id, g.name]));
  const row = (group, en) => ({
    'Client Group': group,
    'Entity Name': en ? en.entityName : '',
    UEN: en ? en.uen || '' : '',
    'Entity Category': en ? ENTITY_CATEGORY_LABELS[en.entityCategory] || en.entityCategory || '' : '',
    Relationship: en ? (en.active ? 'Active' : 'Inactive') : '',
    'Date of Appointment': en && en.dateOfAppointment ? formatDate(en.dateOfAppointment) : '',
    'Inactive Date': en && en.inactiveDate ? formatDate(en.inactiveDate) : '',
    'Inactive Reason': en ? en.inactiveReason || '' : '',
  });
  const rows = [];
  [...groups].sort((a, b) => byName(a.name, b.name)).forEach((g) => {
    const members = entities.filter((en) => en.clientGroupId === g.id)
      .sort((a, b) => byName(a.entityName, b.entityName));
    if (members.length === 0) rows.push(row(g.name, null));
    else members.forEach((en) => rows.push(row(g.name, en)));
  });
  entities.filter((en) => !en.clientGroupId || !groupName.has(en.clientGroupId))
    .sort((a, b) => byName(a.entityName, b.entityName))
    .forEach((en) => rows.push(row(en.clientGroupName || 'Ungrouped', en)));
  exportToExcel(rows, 'Clients_and_Entities.xlsx');
};

// One entity sub-table per client group. Each instance owns its own sort state
// so sorting one group never reorders another.
// No Delete on a company: the master never loses a company — a company the
// firm stops serving is marked Inactive (here, or through a Client Amendment).
const EntityTable = ({ rows, onEdit, onHistory, onLetters, onMarkInactive, onReactivate }) => {
  const { rows: page1, sortConfig, requestSort, page, setPage, total } = usePagedSortableData(rows);
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <SortableHeader label="Entity Name" sortKey="entityName" sortConfig={sortConfig} onSort={requestSort} className="pl-12 pr-4 py-2 text-xs font-semibold text-slate-500 uppercase" />
            <SortableHeader label="UEN" sortKey="uen" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />
            <SortableHeader label="Category" sortKey="entityCategory" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />
            <SortableHeader label="Relationship" sortKey="active" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />
            <SortableHeader label="Actions" sortable={false} align="right" className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {page1.map(en => (
            <tr key={en.id} className="hover:bg-slate-100/60">
              <td className={`pl-12 pr-4 py-2 ${en.active ? 'text-slate-900' : 'text-slate-500'}`}>{en.entityName}</td>
              <td className={`px-4 py-2 whitespace-nowrap ${en.active ? 'text-slate-700' : 'text-slate-400'}`}>{en.uen}</td>
              <td className="px-4 py-2 whitespace-nowrap"><EntityCategoryChip value={en.entityCategory} /></td>
              <td className="px-4 py-2 whitespace-nowrap"><RelationshipBadge entity={en} /></td>
              <td className="px-4 py-2 text-right">
                <div className="inline-flex gap-1">
                  <button
                    onClick={() => onLetters(en)}
                    title="Appointment letters"
                    className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onHistory(en)}
                    title="History"
                    className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <History className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onEdit(en)}
                    title="Edit"
                    className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {en.active ? (
                    <button
                      onClick={() => onMarkInactive(en)}
                      title="Mark as Inactive"
                      className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded"
                    >
                      <Ban className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => onReactivate(en)}
                      title="Reactivate"
                      className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <TablePager
        page={page}
        setPage={setPage}
        total={total}
        className="flex items-center justify-between pl-12 pr-4 py-2 border-t border-slate-200"
      />
    </div>
  );
};

// Field-level history of one entity — the timeline itself is the shared
// ClientEntityHistory (the Client Hub renders the same one); this is just the
// modal around it.
const EntityHistoryModal = ({ entity, onClose }) => (
  <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900">History — {entity.entityName}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6">
        <ClientEntityHistory fetchPath={`/api/settings/client-entities/${entity.id}/events`} />
      </div>
    </div>
  </div>
);

const SettingsClients = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('settings:clients:edit');

  // Client groups (all, including inactive — this is the management page)
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [structureGroup, setStructureGroup] = useState(null);

  // Entities (all — grouped client-side under their client group)
  const [entities, setEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [entitiesError, setEntitiesError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [creatingEntityGroup, setCreatingEntityGroup] = useState(null);
  const [editingEntity, setEditingEntity] = useState(null);
  const [historyEntity, setHistoryEntity] = useState(null);
  const [lettersEntity, setLettersEntity] = useState(null);
  const [markingInactiveEntity, setMarkingInactiveEntity] = useState(null);
  const [reactivatingEntity, setReactivatingEntity] = useState(null);

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    setGroupsError(null);
    try {
      const data = await fetchJSON('/api/settings/client-groups');
      setGroups(data.groups || []);
    } catch (e) { setGroupsError(e.message); }
    finally { setGroupsLoading(false); }
  }, []);

  const loadEntities = useCallback(async () => {
    setEntitiesLoading(true);
    setEntitiesError(null);
    try {
      const data = await fetchJSON('/api/settings/client-entities');
      setEntities(data.entities || []);
    } catch (e) { setEntitiesError(e.message); }
    finally { setEntitiesLoading(false); }
  }, []);

  useEffect(() => {
    if (!canEdit) return;
    loadGroups();
  }, [canEdit, loadGroups]);

  useEffect(() => {
    if (!canEdit) return;
    loadEntities();
  }, [canEdit, loadEntities]);

  const toggleGroup = (key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Group tree under the toolbar filters. Relationship and category filter
  // companies; a group is listed when any of its companies passes (only those
  // shown), and an empty group only while it is still active. The search box
  // also keeps a group whose own name matches (its passing companies shown).
  // Any filter beyond the default Active view auto-expands the visible groups.
  const term = search.trim().toLowerCase();
  const tree = useMemo(() => {
    const byGroup = new Map();
    const ungrouped = [];
    entities.forEach(en => {
      if (en.clientGroupId) {
        if (!byGroup.has(en.clientGroupId)) byGroup.set(en.clientGroupId, []);
        byGroup.get(en.clientGroupId).push(en);
      } else {
        ungrouped.push(en);
      }
    });

    const entityMatches = (en) =>
      en.entityName.toLowerCase().includes(term) || (en.uen || '').toLowerCase().includes(term);
    const statusOk = (en) =>
      statusFilter === 'all' || (statusFilter === 'active' ? en.active : !en.active);
    const categoryOk = (en) => !categoryFilter || en.entityCategory === categoryFilter;

    // Companies passing search + category, before the relationship filter —
    // the pool the status switch counts.
    const pool = [];
    const groupRows = groups.map(g => {
      const all = byGroup.get(g.id) || [];
      const nameHit = !term || g.name.toLowerCase().includes(term);
      const candidates = all.filter(en => categoryOk(en) && (nameHit || entityMatches(en)));
      pool.push(...candidates);
      const shown = candidates.filter(statusOk);
      if (shown.length > 0) return { group: g, entities: shown, totalCount: all.length };
      const emptyGroupListed = all.length === 0 && nameHit && !categoryFilter
        && (statusFilter === 'all' || (statusFilter === 'active' && g.active));
      return emptyGroupListed ? { group: g, entities: [], totalCount: 0 } : null;
    }).filter(Boolean);

    const ungroupedPool = ungrouped.filter(en => categoryOk(en) && (!term || entityMatches(en)));
    pool.push(...ungroupedPool);
    const ungroupedRows = ungroupedPool.filter(statusOk);
    // Counts shown next to the search box — what the filters currently match,
    // counting the entities actually listed under each visible group.
    const entityCount = groupRows.reduce((n, r) => n + r.entities.length, 0) + ungroupedRows.length;
    const activeCount = pool.filter(en => en.active).length;
    return {
      groupRows, ungroupedRows, ungroupedTotal: ungrouped.length,
      groupCount: groupRows.length, entityCount,
      statusCounts: { active: activeCount, inactive: pool.length - activeCount },
    };
  }, [groups, entities, term, statusFilter, categoryFilter]);

  // The group tree itself pages PAGE_SIZE groups at a time (each expanded
  // group pages its own entities inside EntityTable). A new search term
  // changes the row count and so lands back on page 1.
  const groupPager = usePagedSortableData(tree.groupRows);

  if (!canEdit) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need master data editing rights to access this area.</p>
        </div>
      </div>
    );
  }

  const loading = groupsLoading || entitiesLoading;
  const anyError = groupsError || entitiesError;
  const filtering = !!(term || categoryFilter || statusFilter !== 'active');
  const isExpanded = (key) => (filtering ? true : expanded.has(key));
  const hasFilters = !!(search || categoryFilter);
  const clearFilters = () => { setSearch(''); setCategoryFilter(''); };
  const statusOptions = STATUS_FILTERS.map((f) => ({
    ...f,
    count: loading ? null
      : f.key === 'all' ? tree.statusCounts.active + tree.statusCounts.inactive
        : tree.statusCounts[f.key],
  }));
  const reloadAll = () => { loadGroups(); loadEntities(); };
  const entityTableProps = {
    onEdit: setEditingEntity,
    onHistory: setHistoryEntity,
    onLetters: setLettersEntity,
    onMarkInactive: setMarkingInactiveEntity,
    onReactivate: setReactivatingEntity,
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Clients & Entities</h1>
        <p className="text-sm text-slate-500 mt-1">Create a client group first, then add its entities under it. Engagement forms pick from these lists.</p>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Client Groups</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => exportAllToExcel(groups, entities)}
              disabled={loading || !!anyError || (groups.length === 0 && entities.length === 0)}
              title="Download every client group and entity, regardless of the filters below"
              className={exportExcelBtnCls}
            >
              <Download className="w-4 h-4" /> Export Excel
            </button>
            <button
              onClick={() => setShowCreateGroup(true)}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> New Group
            </button>
          </div>
        </div>
        <div className="p-3 border-b border-slate-200">
          <div className="flex flex-wrap items-center gap-3">
            <SegmentedSwitch
              ariaLabel="Relationship"
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
            />
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search group name, entity name or UEN"
                className={`${inputCls} pl-9`}
              />
            </div>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className={`${inputCls} sm:w-60`} aria-label="Entity Category">
              <option value="">All Categories</option>
              {ENTITY_CATEGORIES.map(k => <option key={k} value={k}>{ENTITY_CATEGORY_LABELS[k]}</option>)}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
              >
                Clear Filters
              </button>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <ResultCount count={tree.groupCount} noun="group" loading={loading} />
              <span className="text-slate-300">·</span>
              <ResultCount count={tree.entityCount} noun="entity" plural="entities" loading={loading} />
            </div>
          </div>
        </div>
        {anyError && (
          <div className="m-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{anyError}</div>
        )}
        {loading ? (
          <div className="px-4 py-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : tree.groupRows.length === 0 && tree.ungroupedRows.length === 0 ? (
          <div className="px-4 py-8 text-center text-slate-500 text-sm">
            {filtering ? 'No groups or entities match these filters.' : 'No client groups found.'}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {groupPager.rows.map(({ group: g, entities: groupEntities, totalCount }) => (
              <div key={g.id}>
                <div
                  className="flex items-center gap-2 px-4 py-2.5 hover:bg-slate-50 cursor-pointer select-none"
                  onClick={() => toggleGroup(g.id)}
                >
                  {isExpanded(g.id)
                    ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                  <span className="font-medium text-slate-900">{g.name}</span>
                  <StatusBadge active={g.active} />
                  <span className="text-xs text-slate-500">
                    {filtering && groupEntities.length !== totalCount
                      ? `${groupEntities.length} of ${totalCount} entities`
                      : `${totalCount} ${totalCount === 1 ? 'entity' : 'entities'}`}
                  </span>
                  <div className="ml-auto inline-flex gap-1" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => setStructureGroup(g)}
                      title="Group structure diagram"
                      className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Network className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCreatingEntityGroup(g)}
                      title="Add entity"
                      className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingGroup(g)}
                      title="Edit group"
                      className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {/* Only an EMPTY group can go — a group with companies
                        is never deleted (its companies are inactive instead). */}
                    {totalCount === 0 ? (
                      <button
                        onClick={() => setDeletingGroup(g)}
                        title="Delete group"
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <LockTip text="Has entities — only an empty group can be deleted; mark its entities Inactive instead" align="right">
                        <span className="p-1.5 text-slate-300"><Trash2 className="w-4 h-4" /></span>
                      </LockTip>
                    )}
                  </div>
                </div>
                {isExpanded(g.id) && (
                  <div className="bg-slate-50/60 border-t border-slate-100">
                    {groupEntities.length === 0 ? (
                      <p className="pl-12 pr-4 py-3 text-sm text-slate-500">No entities in this group yet — use the + button to add one.</p>
                    ) : <EntityTable rows={groupEntities} {...entityTableProps} />}
                  </div>
                )}
              </div>
            ))}
            <TablePager {...groupPager} noun="groups" className="flex items-center justify-between px-4 py-2" />

            {tree.ungroupedRows.length > 0 && (
              <div>
                <div
                  className="flex items-center gap-2 px-4 py-2.5 hover:bg-slate-50 cursor-pointer select-none"
                  onClick={() => toggleGroup('ungrouped')}
                >
                  {isExpanded('ungrouped')
                    ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                  <span className="font-medium text-slate-500 italic">Ungrouped</span>
                  <span className="text-xs text-slate-500">
                    {filtering && tree.ungroupedRows.length !== tree.ungroupedTotal
                      ? `${tree.ungroupedRows.length} of ${tree.ungroupedTotal} entities`
                      : `${tree.ungroupedTotal} ${tree.ungroupedTotal === 1 ? 'entity' : 'entities'}`}
                  </span>
                  <span className="text-xs text-slate-500 hidden sm:inline">— edit an entity to assign it to a group</span>
                </div>
                {isExpanded('ungrouped') && (
                  <div className="bg-slate-50/60 border-t border-slate-100">
                    <EntityTable rows={tree.ungroupedRows} {...entityTableProps} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {(showCreateGroup || editingGroup) && (
        <GroupModal
          group={editingGroup}
          onClose={() => { setShowCreateGroup(false); setEditingGroup(null); }}
          onSaved={() => {
            setShowCreateGroup(false);
            setEditingGroup(null);
            loadGroups();
            loadEntities();
          }}
        />
      )}
      {deletingGroup && (
        <DeleteGroupModal
          group={deletingGroup}
          onClose={() => setDeletingGroup(null)}
          onDeleted={() => { setDeletingGroup(null); loadGroups(); loadEntities(); }}
        />
      )}
      {structureGroup && (
        <GroupStructureModal
          group={structureGroup}
          canEdit={canEdit}
          onClose={() => setStructureGroup(null)}
        />
      )}
      {(creatingEntityGroup || editingEntity) && (
        <EntityModal
          entity={editingEntity}
          defaultGroupId={creatingEntityGroup?.id}
          groups={groups}
          onClose={() => { setCreatingEntityGroup(null); setEditingEntity(null); }}
          onSaved={(savedGroupId) => {
            setCreatingEntityGroup(null);
            setEditingEntity(null);
            // A relationship change can flip the group's derived status.
            reloadAll();
            // Reveal the entity that was just saved
            if (savedGroupId) setExpanded(prev => new Set(prev).add(savedGroupId));
          }}
        />
      )}
      {markingInactiveEntity && (
        <MarkInactiveEntityModal
          entity={markingInactiveEntity}
          onClose={() => setMarkingInactiveEntity(null)}
          onDone={() => { setMarkingInactiveEntity(null); reloadAll(); }}
        />
      )}
      {reactivatingEntity && (
        <ReactivateEntityModal
          entity={reactivatingEntity}
          onClose={() => setReactivatingEntity(null)}
          onDone={() => { setReactivatingEntity(null); reloadAll(); }}
        />
      )}
      {historyEntity && (
        <EntityHistoryModal entity={historyEntity} onClose={() => setHistoryEntity(null)} />
      )}
      {lettersEntity && (
        <AppointmentLettersModal entity={lettersEntity} canEdit={canEdit} onClose={() => setLettersEntity(null)} />
      )}
    </div>
  );
};

const GroupModal = ({ group, onClose, onSaved }) => {
  const isEdit = !!group;
  const [name, setName] = useState(group?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // 空白的必填格：按 Save 後就地標紅並列進橫幅，填好就消。
  const gaps = useMemo(() => (name.trim() ? [] : [{ key: 'name', label: 'Name' }]), [name]);
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await fetchJSON(`/api/settings/client-groups/${group.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ name: name.trim() }),
        });
      } else {
        await fetchJSON('/api/settings/client-groups', {
          method: 'POST',
          body: JSON.stringify({ name: name.trim() }),
        });
      }
      onSaved();
    } catch (e) {
      if (e.code === 'DUPLICATE_NAME') {
        setError('A client group with this name already exists.');
      } else {
        setError(e.message);
      }
    }
    finally { setSaving(false); }
  };

  return (
    <FormModal
      title={isEdit ? `Edit ${group.name}` : 'New Client Group'}
      size="sm"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} className={btnPrimary}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
          <FormValidationProvider value={validation.providerValue}>
          <Field label="Name" required field="name">
            <input type="text" value={name} onChange={e => setName(e.target.value)} className={inputCls} />
          </Field>
          {isEdit && (
            <p className="text-xs text-slate-500">
              Relationship: <StatusBadge active={group.active} /> — a group is active while any of its companies is; mark the companies inactive or reactivate them.
            </p>
          )}
          {(error || validation.error) && <ErrorBanner>{error || validation.error}</ErrorBanner>}
          </FormValidationProvider>
    </FormModal>
  );
};

const DeleteGroupModal = ({ group, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [inUse, setInUse] = useState(false);

  const confirmDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await fetchJSON(`/api/settings/client-groups/${group.id}`, { method: 'DELETE' });
      onDeleted();
    } catch (e) {
      if (e.code === 'IN_USE') setInUse(true);
      setError(e.message);
    }
    finally { setDeleting(false); }
  };

  return (
    <FormModal
      title="Delete Client Group"
      size="sm"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>{inUse ? 'Close' : 'Cancel'}</button>
          {!inUse && (
            <button type="button" onClick={confirmDelete} disabled={deleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50">
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
        </>
      )}
    >
          <p className="text-sm text-slate-700">
            Are you sure you want to delete <span className="font-semibold">{group.name}</span>? This action cannot be undone.
            Groups that are still referenced cannot be deleted — deactivate them instead.
          </p>
          {error && <ErrorBanner>{error}</ErrorBanner>}
    </FormModal>
  );
};

const EntityModal = ({ entity, groups, defaultGroupId, onClose, onSaved }) => {
  const isEdit = !!entity;
  const activeGroups = groups.filter(g => g.active);

  const today = firmToday();
  const [form, setForm] = useState({
    entityName: entity?.entityName || '',
    uen: entity?.uen || '',
    // New entities start as a Singapore Company (the common case); an existing
    // row keeps whatever it has.
    entityCategory: entity ? (entity.entityCategory || '') : 'local',
    clientGroupId: entity?.clientGroupId
      ? String(entity.clientGroupId)
      : (defaultGroupId ? String(defaultGroupId) : ''),
    active: entity ? !!entity.active : true,
    inactiveDate: entity?.inactiveDate || '',
    inactiveReason: entity?.inactiveReason || '',
    // Mandatory on a new company; a company registered before the field
    // existed may stay blank until someone fills it in.
    dateOfAppointment: entity?.dateOfAppointment || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Shown when Active is picked while an inactive date is still filled in.
  const [clearFirst, setClearFirst] = useState(false);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const pickRelationship = (key) => {
    if (key === 'active' && form.inactiveDate) {
      setClearFirst(true);
      return;
    }
    setClearFirst(false);
    setField('active', key === 'active');
  };

  const setInactiveDate = (v) => {
    setForm(prev => ({ ...prev, inactiveDate: v, active: v ? false : prev.active }));
    if (v) setClearFirst(false);
  };

  const clearInactiveDate = () => {
    setField('inactiveDate', '');
    setClearFirst(false);
  };

  const dateError = !form.active ? inactiveDateError(form.inactiveDate) : null;

  // Keep an entity's current (possibly inactive) group visible while editing.
  const currentGroupMissing = form.clientGroupId
    && !activeGroups.some(g => String(g.id) === form.clientGroupId);
  const currentGroup = groups.find(g => String(g.id) === form.clientGroupId);

  // Marking a company inactive that still has an engagement in progress is refused by
  // the server — say so before Save, next to the switch.
  const markingInactive = isEdit && entity.active && !form.active;
  const inProgress = entity?.inProgressEngagementCount || 0;

  // 空白的必填格：按 Save 後就地標紅並列進橫幅，填好就消（頁面順序）。
  const gaps = useMemo(() => {
    const g = [];
    if (!form.clientGroupId) g.push({ key: 'clientGroupId', label: 'Client Group' });
    if (!form.entityName.trim()) g.push({ key: 'entityName', label: 'Entity Name' });
    if (!form.entityCategory) g.push({ key: 'entityCategory', label: 'Entity Category' });
    if (!form.uen.trim()) g.push({ key: 'uen', label: 'UEN' });
    if (!isEdit && !form.dateOfAppointment) g.push({ key: 'dateOfAppointment', label: 'Date of Appointment' });
    if (!form.active && !form.inactiveDate) g.push({ key: 'inactiveDate', label: 'Inactive Date' });
    return g;
  }, [form, isEdit]);
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    if (dateError) { setError(dateError); return; }
    setSaving(true);
    setError(null);
    const body = {
      entityName: form.entityName.trim(),
      uen: form.uen.trim().toUpperCase(),
      entityCategory: form.entityCategory,
      clientGroupId: Number(form.clientGroupId),
      dateOfAppointment: form.dateOfAppointment || null,
    };
    if (isEdit) {
      body.active = form.active;
      if (!form.active) {
        body.inactiveDate = form.inactiveDate;
        body.inactiveReason = form.inactiveReason.trim();
      }
    }
    try {
      if (isEdit) {
        await fetchJSON(`/api/settings/client-entities/${entity.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        await fetchJSON('/api/settings/client-entities', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      onSaved(body.clientGroupId);
    } catch (e) {
      if (e.code === 'DUPLICATE_ENTITY') {
        setError('An entity with this name already exists.');
      } else if (e.code === 'DUPLICATE_UEN') {
        setError('An entity with this UEN already exists.');
      } else {
        setError(e.message);
      }
    }
    finally { setSaving(false); }
  };

  return (
    <FormModal
      title={isEdit ? `Edit ${entity.entityName}` : 'New Entity'}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} className={btnPrimary}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
          <FormValidationProvider value={validation.providerValue}>
          <Field label="Client Group" required field="clientGroupId">
            <SearchSelect
              value={form.clientGroupId}
              onChange={(v) => setField('clientGroupId', v === '' ? '' : String(v))}
              options={[
                ...activeGroups.map(g => ({ value: String(g.id), label: g.name })),
                ...(currentGroupMissing
                  ? [{ value: form.clientGroupId, label: currentGroup?.name || entity?.clientGroupName || `Group #${form.clientGroupId}` }]
                  : []),
              ]}
              placeholder="Search group…"
            />
          </Field>
          <Field label="Entity Name" required field="entityName">
            <input type="text" value={form.entityName} onChange={e => setField('entityName', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Entity Category" required field="entityCategory" hint="Where the company is registered — not whether the engagement's client is a PIE.">
            <EntityCategorySelect value={form.entityCategory} onChange={(v) => setField('entityCategory', v)} className={inputCls} />
          </Field>
          <Field label="UEN" required field="uen" hint={uenHintFor(form.entityCategory)}>
            <input
              type="text"
              value={form.uen}
              onChange={e => setField('uen', e.target.value)}
              onBlur={e => setField('uen', e.target.value.trim().toUpperCase())}
              className={inputCls}
            />
          </Field>
          <Field
            label="Date of Appointment"
            required={!isEdit}
            field="dateOfAppointment"
            hint={isEdit && !form.dateOfAppointment
              ? 'Not recorded (registered before the field existed) — fill it in when known. Engagements read it from here.'
              : 'The date this company appointed the firm — engagements read it from here instead of asking each time.'}
          >
            <input type="date" value={form.dateOfAppointment} onChange={e => setField('dateOfAppointment', e.target.value)} className={inputCls} />
          </Field>
          {isEdit && (
            <Field label="Relationship">
              <SegmentedSwitch
                ariaLabel="Relationship"
                options={RELATIONSHIPS}
                value={form.active ? 'active' : 'inactive'}
                onChange={pickRelationship}
              />
            </Field>
          )}
          {clearFirst && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-amber-800 text-sm">
              <span>Clear the inactive date first to mark this company as an active client.</span>
            </div>
          )}
          {isEdit && !form.active && (
            <>
              {markingInactive && inProgress > 0 && <InProgressWarning count={inProgress} />}
              <Field
                label="Inactive Date"
                required
                field="inactiveDate"
                error={dateError}
                action={form.inactiveDate ? (
                  <button type="button" onClick={clearInactiveDate} className="text-xs text-blue-600 hover:text-blue-800">
                    Clear Date
                  </button>
                ) : null}
              >
                <input
                  type="date"
                  value={form.inactiveDate}
                  max={today}
                  onChange={e => setInactiveDate(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Reason">
                <textarea rows="2" value={form.inactiveReason} onChange={e => setField('inactiveReason', e.target.value)} className={inputCls} />
              </Field>
            </>
          )}
          {(error || validation.error) && <ErrorBanner>{error || validation.error}</ErrorBanner>}
          </FormValidationProvider>
    </FormModal>
  );
};

// The server refuses to mark a company inactive with an engagement still open — said
// before Save, next to the inactive date.
const InProgressWarning = ({ count }) => (
  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-amber-800 text-sm">
    <span>
      {count} engagement{count === 1 ? ' is' : 's are'} still in progress on this company — it cannot be marked inactive until {count === 1 ? 'it is' : 'they are'} closed (Engagement Status Change, or Management → Engagements).
    </span>
  </div>
);

const SmallModal = ({ title, onClose, children }) => (
  <div className={modalOverlayCls}>
    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div className="flex items-center justify-between p-6 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-6 space-y-4">{children}</div>
    </div>
  </div>
);

const ErrorBanner = ({ children }) => (
  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
    <span>{children}</span>
  </div>
);

const MarkInactiveEntityModal = ({ entity, onClose, onDone }) => {
  const today = firmToday();
  const [inactiveDate, setInactiveDate] = useState(today);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const inProgress = entity.inProgressEngagementCount || 0;
  const dateError = inactiveDateError(inactiveDate);
  const validation = useFormValidation(inactiveDate ? [] : [{ key: 'inactiveDate', label: 'Inactive Date' }]);

  const confirm = async () => {
    if (!validation.validate() || dateError) return;
    setSubmitting(true);
    setError(null);
    try {
      await fetchJSON(`/api/settings/client-entities/${entity.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: false, inactiveDate, inactiveReason: reason.trim() }),
      });
      onDone();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <SmallModal title="Mark as Inactive" onClose={onClose}>
      <p className="text-sm text-slate-700">
        Mark <span className="font-semibold">{entity.entityName}</span> as inactive?
        It stays on every existing engagement and record, but cannot be put on a new one.
      </p>
      {inProgress > 0 && <InProgressWarning count={inProgress} />}
      <FormValidationProvider value={validation.providerValue}>
        <Field label="Inactive Date" required field="inactiveDate" error={dateError}>
          <input type="date" value={inactiveDate} max={today} onChange={e => setInactiveDate(e.target.value)} className={inputCls} />
        </Field>
      </FormValidationProvider>
      <Field label="Reason">
        <textarea rows="2" value={reason} onChange={e => setReason(e.target.value)} className={inputCls} />
      </Field>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className={btnGhost}>Cancel</button>
        <button
          onClick={confirm}
          disabled={submitting || inProgress > 0}
          className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50"
        >
          {submitting ? 'Saving…' : 'Mark as Inactive'}
        </button>
      </div>
    </SmallModal>
  );
};

const ReactivateEntityModal = ({ entity, onClose, onDone }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await fetchJSON(`/api/settings/client-entities/${entity.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: true }),
      });
      onDone();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <SmallModal title="Reactivate Client" onClose={onClose}>
      <p className="text-sm text-slate-700">
        Reactivate <span className="font-semibold">{entity.entityName}</span>? The inactive
        date <span className="font-semibold">{formatDate(entity.inactiveDate)}</span>
        {entity.inactiveReason ? ' and its reason' : ''} will be cleared, and the company can be put on new engagements again.
      </p>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className={btnGhost}>Cancel</button>
        <button onClick={confirm} disabled={submitting} className={btnPrimary}>
          {submitting ? 'Saving…' : 'Reactivate'}
        </button>
      </div>
    </SmallModal>
  );
};


const fmtBytes = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Versioned group-structure diagrams for one client group:
// view the history, upload a new version (never overwrites), download any.
const GroupStructureModal = ({ group, canEdit, onClose }) => {
  const [structures, setStructures] = useState(null);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await fetchJSON(`/api/settings/client-groups/${group.id}/structures`);
      setStructures(data.structures || []);
    } catch (e) { setError(e.message); }
  }, [group.id]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/settings/client-groups/${group.id}/structures`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
      setFile(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Group Structure — {group.name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-slate-500">
            The group's structure diagram, kept as versions — a new upload is added, older versions are retained. Group-audit Setup requests adopt the latest version or submit a new one for review.
          </p>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <span>{error}</span>
            </div>
          )}
          {canEdit && (
            <div className="flex items-end gap-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-medium text-slate-600 mb-1">Upload a new version</label>
                <FilePicker
                  kind="docsAndImages"
                  onChange={e => setFile((e.target.files || [])[0] || null)}
                />
              </div>
              <button
                onClick={upload}
                disabled={!file || uploading}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
              >
                <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          )}
          {structures === null ? (
            <p className="text-sm text-slate-500 text-center py-6">Loading…</p>
          ) : structures.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">No group structure uploaded yet.</p>
          ) : (
            <FileList
              files={structures}
              row={(s, i) => ({
                name: s.originalName,
                stacked: true,
                badges: [i === 0 ? ['CURRENT', 'bg-green-50 text-green-700'] : null],
                meta: [formatDateTime(s.createdAt), fmtBytes(s.sizeBytes)],
                actions: (
                  <DownloadLink
                    href={`${API_BASE_URL}/api/settings/client-groups/${group.id}/structures/${s.id}/download`}
                  />
                ),
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Appointment letters of one entity.
// Current letter first, superseded history below; manual upload may cover
// several same-group entities (this one always included). Workflow-produced
// letters (NC / CAR approvals) land here too.
const AppointmentLettersModal = ({ entity, canEdit, onClose }) => {
  const [letters, setLetters] = useState(null);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await fetchJSON(`/api/settings/client-entities/${entity.id}/appointment-letters`);
      setLetters(data.letters || []);
    } catch (e) { setError(e.message); }
  }, [entity.id]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/settings/client-entities/${entity.id}/appointment-letters`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
      setFile(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Appointment Letters — {entity.entityName}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-slate-500">
            The client's appointment letters, kept as versions — the current one on top, superseded letters below.
            New Client registrations land here; a manual upload supersedes the current letter.
          </p>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <span>{error}</span>
            </div>
          )}
          {canEdit && (
            <div className="flex items-end gap-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-medium text-slate-600 mb-1">Upload a new letter (supersedes the current one)</label>
                <FilePicker
                  kind="docsAndImages"
                  onChange={e => setFile((e.target.files || [])[0] || null)}
                />
              </div>
              <button
                onClick={upload}
                disabled={!file || uploading}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
              >
                <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          )}
          {letters === null ? (
            <p className="text-sm text-slate-500 text-center py-6">Loading…</p>
          ) : letters.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">No appointment letter on file yet.</p>
          ) : (
            <FileList
              files={letters}
              row={(l) => ({
                name: l.originalName,
                stacked: true,
                badges: [l.superseded
                  ? ['superseded', 'bg-slate-100 text-slate-500']
                  : ['CURRENT', 'bg-green-50 text-green-700']],
                meta: [
                  formatDateTime(l.createdAt),
                  fmtBytes(l.sizeBytes),
                  l.sourceRequestNo
                    ? <WorkflowRefLink refNo={l.sourceRequestNo} />
                    : (l.source !== 'manual' ? (provenanceLabel(l.source) || l.source) : null),
                ],
                actions: (
                  <DownloadLink
                    href={`${API_BASE_URL}/api/settings/client-entities/${entity.id}/appointment-letters/${l.id}/download`}
                  />
                ),
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsClients;
